import { Box, Typography } from '@mui/material';
import { E_COMMERCE_STYLES } from 'components/ECommerce/config';
import CTAButtons from 'components/mobileLanding/elements/CTAButtons';
import React from 'react';

interface DiscussProjectSectionProps {
  type: string;
  title?: any;
  description?: string;
}

const features = [
  {
    emoji: '💡',
    title: 'Refine your idea with our experienced team',
    description: `We'll listen, ask the right questions and help you sharpen your concept into something practical and buildable.`,
  },
  {
    emoji: '🧩',
    title: 'Get a basic system blueprint',
    description:
      'A rough picture of how a tech-driven system for your business could work (modules, flows, key screens).',
  },
  {
    emoji: '📊',
    title: 'Get a roadmap & budget range',
    description:
      'A step-by-step plan with phases and an approximate investment, so you can decide calmly if/when to move forward.',
  },
];

const DiscussProjectSection: React.FC<DiscussProjectSectionProps> = ({
  type,
  title,
  description,
}) => {
  return (
    <Box
      sx={{
        padding: type === 'web' ? '80px 24px 50px' : '40px 24px 50px',
        position: 'relative',
      }}
    >
      {type === 'web' ? (
        <Box
          sx={{
            maxWidth: E_COMMERCE_STYLES.container.maxWidthWeb,
            margin: E_COMMERCE_STYLES.container.mx,
            px: 12,
          }}
        >
          {/* Web Layout: Two Column */}
          <Box
            sx={{
              display: 'flex',
              gap: 15,
              mb: 6,
              alignItems: 'flex-start',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            {/* Left Column: Title and Subtitle */}
            <Box sx={{ flex: '0 0 auto', maxWidth: '400px' }}>
              <Typography
                variant="h2"
                className="font-satoshi-bold"
                sx={{
                  background: 'linear-gradient(270.11deg, #177BFF 23.63%, #0F1C3F 123.03%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontSize: '48px',
                  lineHeight: '110%',
                  pb: 2,
                }}
              >
                {title || (
                  <>
                    Discuss <br /> Your Project <br /> With Us
                  </>
                )}
              </Typography>

              <Typography
                className="font-satoshi-italic"
                sx={{
                  color: '#00000099',
                  fontSize: '16px',
                  fontStyle: 'italic',
                  lineHeight: '150%',
                }}
              >
                {description || (
                  <>
                    {`You don't have to decide the whole project today.`}
                    <br />
                    Just take the next sensible step and get clarity.
                  </>
                )}
              </Typography>
            </Box>

            {/* Right Column: Features */}
            <Box
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '430px' }}
            >
              {features.map((feature, index) => (
                <Box key={index}>
                  {/* Feature Title */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                    <Typography sx={{ fontSize: '20px', lineHeight: 1 }}>
                      {feature.emoji}
                    </Typography>
                    <Typography
                      className="font-satoshi-bold"
                      sx={{
                        color: '#1a1a1a',
                        fontSize: '16px',
                        fontWeight: 700,
                        lineHeight: '130%',
                      }}
                    >
                      {feature.title}
                    </Typography>
                  </Box>
                  {/* Feature Description */}
                  <Typography
                    className="font-satoshi-regular"
                    sx={{
                      color: '#00000099',
                      fontSize: '15px',
                      fontWeight: 400,
                      lineHeight: '150%',
                      pl: 5,
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* CTA Buttons - Centered for Web */}
          <CTAButtons
            direction="row"
            scheduleHelperText={
              <>
                {' '}
                {`Pick a time that suits you. We'll discuss your`} <br /> business, not push a sales
                pitch.
              </>
            }
            whatsAppHelperText={
              <>
                Have a quick question? Our team will reply on <br /> WhatsApp.
              </>
            }
            textColor="#666"
            scheduleHelperTextAlign="center"
            whatsAppHelperTextAlign="center"
            containerSx={{ justifyContent: 'center' }}
          />
        </Box>
      ) : (
        <>
          {/* Mobile Layout: Vertical Stack */}
          <Typography
            variant="h2"
            className="font-satoshi-bold"
            sx={{
              background: 'linear-gradient(270.11deg, #177BFF 23.63%, #0F1C3F 123.03%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: '28px',
              lineHeight: '110%',
              mb: 1,
            }}
          >
            Discuss Your Project
            <br />
            With Us
          </Typography>

          <Typography
            className="font-satoshi-italic"
            sx={{
              color: '#00000099',
              fontSize: '14px',
              fontStyle: 'italic',
              lineHeight: '150%',
              mb: 2,
            }}
          >
            {`You don't have to decide the whole project today.`}
            <br />
            Just take the next sensible step and get clarity.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 5 }}>
            {features.map((feature, index) => (
              <Box key={index}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <Typography sx={{ fontSize: '16px', lineHeight: 1 }}>{feature.emoji}</Typography>
                  <Typography
                    className="font-satoshi-bold"
                    sx={{
                      color: '#1a1a1a',
                      fontSize: '14px',
                      fontWeight: 700,
                      lineHeight: '130%',
                    }}
                  >
                    {feature.title}
                  </Typography>
                </Box>
                <Typography
                  className="font-satoshi-regular"
                  sx={{
                    color: '#00000099',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '150%',
                    pl: 4,
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            ))}
          </Box>

          <CTAButtons scheduleHelperTextAlign="center" whatsAppHelperTextAlign="center" />
        </>
      )}
    </Box>
  );
};

export default DiscussProjectSection;
